import React from 'react';
import { Order, useDutchieStore, useI18n } from '@springbig/shared/dist';
import { Box, ChevronLeftIcon, VStack, HStack, Pressable } from 'native-base';
import { Caption, H2 } from '~/shared/Typography';
import { useShopStyles } from '~/shared/styles';
import Section from '~/shared/Section';
import { format } from 'date-fns';

export type OrderDetailProps = {
    order: Order;
    onCancel: () => void;
};

export const OrderDetailPage = ({
    order,
    onCancel,
}: OrderDetailProps) => {

    const { t } = useI18n();
    const { retailer } = useDutchieStore();
    const {
        button_background,
        button_text,
        pressable_text,
        section_background,
        divider_color
    } = useShopStyles();

    const formatPrice = (price: number): string => {

        return (price / 100).toFixed(2);
    };

    return (
        <Section
            h={'100%'}
            w={'clamp(320px, 100vw, 480px)'}
            overflowX={'hidden'}
            position={'relative'}
        >
            <Pressable
                p={2}
                onPress={onCancel}
            >
                <HStack space={1} alignItems={'center'}>
                    <ChevronLeftIcon color={pressable_text} size={5} />
                    <Caption color={pressable_text} fontSize={16}>
                        { t('shop.back') }
                    </Caption>
                </HStack>
            </Pressable>
            <VStack p={2} space={4}>
                <VStack
                    bg={button_background}
                    rounded={'xl'}
                    p={4}
                    alignItems={'center'}
                    space={2}
                    mx={4}
                >
                    <Caption color={button_text} fontSize={18}>
                        { t('shop.order_number') } { order.orderNumber }
                    </Caption>
                    <Caption color={button_text} fontSize={18}>
                        { format(new Date(order.createdAt), 'EEEE, LLLL d') } { t('shop.at') } { format(new Date(order.createdAt), 'h:maaa') }
                    </Caption>
                </VStack>
                { order.delivery &&
                    <VStack bg={section_background} rounded={'xl'} p={4} space={2}>
                        <H2 my={0}>{ t('shop.delivery') }</H2>
                        <Caption>{ order.status }</Caption>
                    </VStack>
                }
                { order.pickup &&
                    <VStack bg={section_background} rounded={'xl'} p={4} space={2}>
                        <H2 my={0}>{ t('shop.pickup') }</H2>
                        <Caption>{ order.dispensaryName }</Caption>
                        <Caption>{ retailer?.address }</Caption>
                    </VStack>
                }
                <VStack bg={section_background} rounded={'xl'} p={4} space={2}>
                    <H2 my={0}>{ t('shop.items') }</H2>
                    { order.items.map((item, index) =>

                        <HStack key={index} alignItems={'center'} space={2}>
                            <Box w={1} h={1} bg={pressable_text} rounded={'3xl'}></Box>
                            <VStack flex={1}>
                                <Caption fontSize={12}>{ item.product.name }</Caption>
                                <HStack alignSelf={'flex-end'} space={2}>
                                    <Caption>{ formatPrice(item.price) }</Caption>
                                    <Caption>{ t('shop.x') }{ item.quantity }</Caption>
                                    <Caption>{ formatPrice(item.subtotal) }</Caption>
                                </HStack>
                            </VStack>
                        </HStack>
                    )}

                    <Box w={'100%'} h={.5} bg={divider_color}></Box>

                    <VStack space={1}>
                        <HStack justifyContent={'space-between'}>
                            <Caption>{ t('shop.subtotal') }</Caption>
                            <Caption>{ order.subtotal }</Caption>
                        </HStack>
                        { !!order?.fees?.total &&
                            <HStack justifyContent={'space-between'}>
                                <Caption>{ t('shop.fees') }</Caption>
                                <Caption>{ order.fees.total }</Caption>
                            </HStack>
                        }
                        { !!order?.taxes?.total &&
                            <HStack justifyContent={'space-between'}>
                                <Caption>{ t('shop.taxes') }</Caption>
                                <Caption>{ order.taxes.total }</Caption>
                            </HStack>
                        }
                        { !!order?.discounts?.total &&
                            <HStack justifyContent={'space-between'}>
                                <Caption>{ t('shop.discounts') }</Caption>
                                <Caption>-{ order.discounts.total }</Caption>
                            </HStack>
                        }
                        <HStack justifyContent={'space-between'}>
                            <Caption>{ t('shop.total') }</Caption>
                            <Caption>{ order.total }</Caption>
                        </HStack>
                    </VStack>
                </VStack>
            </VStack>
        </Section>
    );
};
